import { isEmptyValue } from "../helper/isEmptyValue";
import type { SyncValidationRule } from "../types/validation";

function stringRule(
  name: string,
  dependencies: readonly unknown[],
  predicate: (value: string) => boolean,
  params?: Record<string, string | number>,
): SyncValidationRule<string | null | undefined> {
  return {
    name,
    kind: "sync",
    dependencies,
    validate(value) {
      if (isEmptyValue(value)) {
        return undefined;
      }
      return typeof value === "string" && predicate(value)
        ? undefined
        : { messageKey: `validation.${name}`, params };
    },
  };
}

export function email(): SyncValidationRule<string | null | undefined> {
  return stringRule("email", [], (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value));
}

export function minLength(min: number): SyncValidationRule<string | null | undefined> {
  return stringRule("minLength", [min], (value) => value.length >= min, { min });
}

export function maxLength(max: number): SyncValidationRule<string | null | undefined> {
  return stringRule("maxLength", [max], (value) => value.length <= max, { max });
}

export function exactLength(length: number): SyncValidationRule<string | null | undefined> {
  return stringRule("exactLength", [length], (value) => value.length === length, { length });
}

export function matches(pattern: RegExp): SyncValidationRule<string | null | undefined> {
  return stringRule("matches", [pattern.source, pattern.flags], (value) => {
    pattern.lastIndex = 0;
    return pattern.test(value);
  });
}

export function digitsOnly(): SyncValidationRule<string | null | undefined> {
  return stringRule("digitsOnly", [], (value) => /^\d+$/.test(value));
}

export function noEdgeWhitespace(): SyncValidationRule<string | null | undefined> {
  return stringRule("noEdgeWhitespace", [], (value) => !/^\s|\s$/.test(value));
}

export function noDoubleSpaces(): SyncValidationRule<string | null | undefined> {
  return stringRule("noDoubleSpaces", [], (value) => !/\s{2,}/.test(value));
}

export function url(maxQueryParams = Number.POSITIVE_INFINITY) {
  return stringRule(
    "url",
    [maxQueryParams],
    (value) => {
      try {
        return [...new URL(value).searchParams.keys()].length <= maxQueryParams;
      } catch {
        return false;
      }
    },
    { maxQueryParams },
  );
}

export function iframeUrl(allowHttp = false): SyncValidationRule<string | null | undefined> {
  return stringRule("iframeUrl", [allowHttp], (value) => {
    try {
      const protocol = new URL(value).protocol;
      return protocol === "https:" || (allowHttp && protocol === "http:");
    } catch {
      return false;
    }
  });
}

export function ltreeSegment(): SyncValidationRule<string | null | undefined> {
  return stringRule("ltreeSegment", [], (value) => !/[{}|()*+\-#?!^$/ /[\]\\äÄöÖüÜß/]/.test(value));
}

export function uniqueNormalizedValue(
  values: readonly unknown[],
): SyncValidationRule<string | null | undefined> {
  return {
    name: "uniqueNormalizedValue",
    kind: "sync",
    dependencies: [values],
    validate(value) {
      if (isEmptyValue(value)) {
        return undefined;
      }
      const normalizedValue = String(value).trim().toLocaleLowerCase();
      return values.some((entry) => String(entry).trim().toLocaleLowerCase() === normalizedValue)
        ? { messageKey: "validation.uniqueNormalizedValue" }
        : undefined;
    },
  };
}
